import React from 'react';
import styled from 'styled-components';
import StyledLink, { StyledCTALink, StyledHeadingLink } from './StyledLink';
import { typography, colors, spaces } from '../../lib/styles';
import Right4 from '../Icon/components/Right4';

// TODO: These types should come from the SPA at some point, until then we can keep it manual
type Size = 'root' | 'subtle' | 'jumbo';
type HeadingLinkType = 'h0' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'h7' | 'inherit';
type FontFamily = 'root' | 'heading' | 'attribution';
export type ColorType =
    | 'chameleon'
    | 'heritage'
    | 'sandyButt'
    | 'risingSun'
    | 'littlePiggy'
    | 'berlinThing'
    | 'lavendork'
    | 'pacific'
    | 'carnaval'
    | 'forrestLyf'
    | 'mildredsCar'
    | 'looseAlmond'
    | 'theTanTan'
    | 'grey800'
    | 'grey700'
    | 'grey500'
    | 'grey300'
    | 'grey100'
    | 'white';

interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
    children?: React.ReactNode;
    size?: Size;
    color?: ColorType;
    fontFamily?: FontFamily;
    isActive?: boolean;
}

interface CTALinkProps extends Props {
    size?: never;
    fontFamily?: never;
}

interface HeadingLinkProps extends Props {
    size?: never;
    type?: HeadingLinkType;
    fontFamily?: never;
}

interface ChameleonLinkProps extends Props {
    color?: never;
}

interface ChameleonCTALinkProps extends CTALinkProps {
    color?: never;
}

interface ChameleonHeadingLinkProps extends HeadingLinkProps {
    color?: never;
}

export interface SubtleLinkProps extends Props {
    size?: never;
    color?: never;
}

export default function Link({
    children,
    color = 'grey800',
    size = 'root',
    fontFamily = 'root',
    ...props
}: Props) {
    return (
        <StyledLink
            linkColor={getLinkColor(color)}
            size={getLinkSize(size)}
            linkFontFamily={getLinkFontFamily(fontFamily)}
            {...props}
        >
            {children}
        </StyledLink>
    );
}

export function ChameleonLink({ children, ...props }: ChameleonLinkProps) {
    return (
        <Link {...props} color="chameleon">
            {children}
        </Link>
    );
}

const RightIcon = styled(Right4)`
    margin-left: ${spaces.inlineIcon}px;
`;

export function CTALink({ children, color = 'grey800', ...props }: CTALinkProps) {
    return (
        <StyledCTALink
            linkColor={getLinkColor(color)}
            size={`${typography.fontSizes.links.jumbo}px`}
            linkFontFamily={typography.fontFamilies.heading}
            {...props}
        >
            {children}
            <RightIcon />
        </StyledCTALink>
    );
}

export function ChameleonCTALink(props: ChameleonCTALinkProps) {
    return <CTALink {...props} color="chameleon" />;
}

export function HeadingLink({
    children,
    color = 'grey800',
    type = 'inherit',
    ...props
}: HeadingLinkProps) {
    return (
        <StyledHeadingLink
            linkColor={getLinkColor(color)}
            linkFontFamily={typography.fontFamilies.heading}
            size={getHeadingLinkSize(type)}
            {...props}
        >
            {children}
        </StyledHeadingLink>
    );
}

export function ChameleonHeadingLink(props: ChameleonHeadingLinkProps) {
    return <HeadingLink {...props} color="chameleon" />;
}

export function SubtleLink({ children, ...props }: SubtleLinkProps) {
    return (
        <Link {...props} color="grey700" size="subtle">
            {children}
        </Link>
    );
}

function getLinkColor(color: ColorType) {
    return color === 'chameleon' ? 'currentColor' : colors[color];
}

function getLinkSize(size: Size) {
    return `${typography.fontSizes.links[size]}px`;
}

function getHeadingLinkSize(type: HeadingLinkType) {
    const { h0, h1, h2, h3, h4, h5, h6, h7 } = typography.fontSizes;
    const sizes: {
        [key: string]: any;
    } = {
        h0,
        h1,
        h2,
        h3,
        h4,
        h5,
        h6,
        h7,
    };

    return sizes[type] ? `${sizes[type].all}px` : type;
}

function getLinkFontFamily(fontFamily: FontFamily) {
    return typography.fontFamilies[fontFamily];
}
